/**
 * "Refresh our prices" control for the comparison toolbar.
 *
 * Shows when our prices were last taken from the store's Magento feed
 * ("Our prices: 18 Oct 04:03") and lets any logged-in user pull the feed
 * again after forcing a regeneration in Magento. The backend only accepts
 * a feed newer than the snapshot it already holds, so the click is safe
 * to repeat; the outcome is shown inline for a few seconds.
 *
 * Outcomes (from POST /api/our-prices/refresh):
 *   updated          — snapshot taken and ingested; the table is refetched
 *   not_regenerated  — the feed is still the one we hold; force it first
 *   busy / throttled — try again shortly
 *   error            — the feed could not be fetched or read
 */
import { useCallback, useEffect, useState } from 'react';
import { useDataFreshness } from '../hooks/useDataFreshness';
import { refreshOurPrices } from '../utils/api';
import { formatLondonClock, formatLondonTimestamp } from '../utils/time';

/** How long an outcome message stays visible. */
const NOTICE_MS = 8000;

const NOTICE_CLASS = {
  ok: 'text-green-700',
  warn: 'text-amber-700',
  error: 'text-red-700',
};

/**
 * Translate a refresh response into a short inline notice.
 *
 * @param {object} result  The API response.
 * @returns {{ tone: 'ok'|'warn'|'error', text: string }}
 */
function describeRefreshOutcome(result) {
  switch (result.status) {
    case 'updated':
      return {
        tone: 'ok',
        text: `Our prices updated from the ${formatLondonClock(result.generated_at)} feed`
          + ` (${result.row_count} rows)`,
      };
    case 'not_regenerated':
      return {
        tone: 'warn',
        text: `Feed has not been regenerated since ${formatLondonTimestamp(result.previous_generated_at)}`
          + ' — force the Magento feed first',
      };
    case 'busy':
      return { tone: 'warn', text: 'Data refresh in progress — try again in a minute' };
    case 'throttled':
      return { tone: 'warn', text: result.message || 'Refreshed moments ago — try again shortly' };
    default:
      return { tone: 'error', text: result.message ? `Refresh failed: ${result.message}` : 'Refresh failed' };
  }
}

/**
 * @param {{ onRefreshed?: Function, mobileOpen?: boolean }} props
 *   onRefreshed is called after an `updated` outcome so the page refetches.
 */
export default function RefreshOurPricesButton({ onRefreshed, mobileOpen }) {
  const { ourPricesFeedAsOf, setOurPricesFeedAsOf } = useDataFreshness();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  // Auto-hide the outcome message.
  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const handleClick = useCallback(async () => {
    setBusy(true);
    setNotice(null);
    try {
      const result = await refreshOurPrices();
      setNotice(describeRefreshOutcome(result));
      if (result.status === 'updated') {
        setOurPricesFeedAsOf(result.generated_at);
        if (onRefreshed) onRefreshed();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'request failed';
      setNotice({ tone: 'error', text: `Refresh failed: ${detail}` });
    } finally {
      setBusy(false);
    }
  }, [onRefreshed, setOurPricesFeedAsOf]);

  const asOfLabel = ourPricesFeedAsOf
    ? `Our prices: ${formatLondonTimestamp(ourPricesFeedAsOf)} feed`
    : 'Our prices: nightly scrape';

  return (
    <div
      className={`${mobileOpen ? 'flex' : 'hidden md:flex'} order-3 md:order-none
                  items-center gap-2 border-l border-gray-200 pl-3`}
    >
      <span
        className="text-xs text-gray-500 whitespace-nowrap"
        title="When our prices were last taken from the store's product feed (UK time)"
      >
        {asOfLabel}
      </span>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        title="Fetch the store feed now (force the Magento feed to regenerate first)"
        className="inline-flex items-center gap-1.5 rounded-md border
                   border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700
                   shadow-sm hover:bg-gray-50 disabled:opacity-50
                   disabled:cursor-not-allowed transition-colors whitespace-nowrap"
      >
        {busy ? (
          <svg className="h-4 w-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2
                 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135
                 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          <svg
            className="h-4 w-4 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993
                 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0
                 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
            />
          </svg>
        )}
        {busy ? 'Refreshing…' : 'Refresh our prices'}
      </button>
      {notice && (
        <span className={`text-xs ${NOTICE_CLASS[notice.tone]} whitespace-nowrap`} role="status">
          {notice.text}
        </span>
      )}
    </div>
  );
}
